import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { AdminSeasonOverview, AdminSeasonRankingEntry } from '../../../models/season-admin';
import { SeasonAdminService } from '../../../services/season-admin.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-season-ranking',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TableModule, AdminPageHeaderComponent],
  templateUrl: './season-ranking.component.html',
  styleUrl: './season-ranking.component.scss',
})
export class SeasonRankingComponent implements OnInit {
  seasonId!: number;
  season?: AdminSeasonOverview;
  ranking: AdminSeasonRankingEntry[] = [];
  totalItems = 0;
  rowsPerPage = 10;
  page = 1;
  isLoading = false;

  searchTerm = '';
  positionFrom?: number;
  positionTo?: number;

  feedbackError = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private seasonService: SeasonAdminService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (!idParam) {
      return;
    }

    this.seasonId = Number(idParam);
    this.loadSeasonInfo();

    this.route.queryParams.subscribe((params) => {
      this.searchTerm = params['search'] || '';
      this.positionFrom = params['position_from'] ? Number(params['position_from']) : undefined;
      this.positionTo = params['position_to'] ? Number(params['position_to']) : undefined;
      const parsedPage = Number(params['page'] || 1);
      this.page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
      this.fetchRanking();
    });
  }

  onSearchChange(): void {
    this.page = 1;
    this.updateQueryParams();
  }

  onFilterChange(): void {
    this.page = 1;
    this.updateQueryParams();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    this.page = Math.floor(first / this.rowsPerPage) + 1;
    this.updateQueryParams();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.positionFrom = undefined;
    this.positionTo = undefined;
    this.page = 1;
    this.updateQueryParams();
  }

  private loadSeasonInfo(): void {
    this.seasonService.detail(this.seasonId).subscribe({
      next: (response) => {
        this.season = response;
      },
    });
  }

  private fetchRanking(): void {
    this.isLoading = true;
    this.feedbackError = '';

    this.seasonService
      .listRanking(this.seasonId, {
        search: this.searchTerm.trim() || undefined,
        position_from: this.positionFrom,
        position_to: this.positionTo,
        page: this.page,
      })
      .subscribe({
        next: (response) => {
          this.ranking = response.results || [];
          this.totalItems = response.count || 0;
          this.isLoading = false;
        },
        error: (error) => {
          this.feedbackError = error?.error?.detail || 'Nao foi possivel carregar o ranking.';
          this.ranking = [];
          this.totalItems = 0;
          this.isLoading = false;
        },
      });
  }

  private updateQueryParams(): void {
    const targetSearch = this.searchTerm || null;
    const targetPositionFrom = this.positionFrom ?? null;
    const targetPositionTo = this.positionTo ?? null;
    const targetPage = this.page;

    const currentPage = this.route.snapshot.queryParamMap.get('page');
    const currentSearch = this.route.snapshot.queryParamMap.get('search');
    const currentPositionFrom = this.route.snapshot.queryParamMap.get('position_from');
    const currentPositionTo = this.route.snapshot.queryParamMap.get('position_to');

    if (
      currentPage === String(targetPage) &&
      currentSearch === (targetSearch === null ? null : String(targetSearch)) &&
      currentPositionFrom === (targetPositionFrom === null ? null : String(targetPositionFrom)) &&
      currentPositionTo === (targetPositionTo === null ? null : String(targetPositionTo))
    ) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: targetSearch,
        position_from: targetPositionFrom,
        position_to: targetPositionTo,
        page: targetPage,
      },
    });
  }
}
